import React, { useRef, useState } from "react";
import { View, Image, StyleSheet, Pressable, Alert } from "react-native";
import { toPng } from "html-to-image";
import {
  assignPatientToClinicImage,
  TReturnGetImagesBatchBatchId,
} from "../../../services/api";
import Download from "../../../assets/icons/Download.icon";
import PersonAdd from "../../../assets/icons/PersonAdd.icon";
import AssignImageToPatientModal from "./modals/AssignImageToPatientModal";
import { TPatientInfo } from "../../../services/api-clinic";
import { SessionContext } from "../../../contexts";
import { mixpanel } from "../../../utils/mixpanel";

const ConcatTwoSquares = ({
  currentImageData,
  onImageGenerated,
  assignableImageId = null,
  imageSize = 200,
}: {
  currentImageData: TReturnGetImagesBatchBatchId[];
  onImageGenerated?: () => void;
  assignableImageId?: number | null;
  imageSize?: number;
}) => {
  const session = React.useContext(SessionContext)?.session;
  const ref = useRef<HTMLDivElement>(null);
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);

  if (!currentImageData?.length || currentImageData[0].presignedUrls.length < 2)
    return null;

  const beforeUrl = currentImageData[0].presignedUrls[0];
  const afterUrl =
    currentImageData[0].presignedUrls[
      currentImageData[0].presignedUrls.length - 1
    ];

  const handleDownload = async () => {
    if (!ref.current) return;
    const dataUrl = await toPng(ref.current);
    const link = document.createElement("a");
    // const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const now = new Date();
    const timestamp = `${now.getFullYear()}-${(now.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}_${now
      .getHours()
      .toString()
      .padStart(2, "0")}-${now.getMinutes().toString().padStart(2, "0")}`;

    link.download = `ClinicOS-side-by-side-${timestamp}.png`;
    link.href = dataUrl;
    link.click();
  };

  const handleAssignToPatient = async (patient: TPatientInfo) => {
    if (!session) return;
    if (!assignableImageId) {
      Alert.alert(
        "Cannot assign image",
        "This simulation is missing an image id. Please regenerate and try again.",
      );
      return;
    }

    try {
      mixpanel.track("Assign Image To Patient Triggered", {
        "Clinic Name": session?.clinicName ?? "clinic not found",
        Flow: "Simulation",
        Endpoint:
          "/clinics/v1/clinic/{clinic_id}/images/{image_id}/assign-patient",
        "Image ID": assignableImageId,
        "Patient UUID": patient.uuid,
      });
      await assignPatientToClinicImage(
        session,
        assignableImageId,
        patient.uuid,
      );
      Alert.alert("Success", "Image assigned to patient.");
    } catch (error) {
      Alert.alert("Assign failed", "Failed to assign image. Please try again.");
      throw error;
    }
  };

  return (
    <View style={styles.container}>
      <div ref={ref} style={styles.concatContainer as any}>
        <img
          src={beforeUrl}
          style={{ ...(styles.imageHalf as any), width: imageSize }}
          alt="Before"
        />
        <div style={styles.separator as any} />
        <img
          src={afterUrl}
          style={{ ...(styles.imageHalf as any), width: imageSize }}
          alt="After"
        />
      </div>
      <View style={styles.actionsRow}>
        <Pressable onPress={handleDownload} style={styles.iconButton}>
          <Download color="#000" />
        </Pressable>
        <Pressable
          onPress={() => setIsAssignModalVisible(true)}
          style={styles.iconButton}
        >
          <PersonAdd size={20} />
        </Pressable>
      </View>

      <AssignImageToPatientModal
        visible={isAssignModalVisible}
        onClose={() => setIsAssignModalVisible(false)}
        onAssign={handleAssignToPatient}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 12,
    alignItems: "center",
  },
  actionsRow: {
    flexDirection: "row",
    gap: 8,
  },
  concatContainer: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: "white",
    padding: 4,
    borderRadius: 10,
  },
  imageHalf: {
    width: 200,
    height: "auto",
    objectFit: "contain",
    borderRadius: 6,
  },
  separator: {
    width: 8,
    backgroundColor: "white",
  },
  iconButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: "#eee",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
  },
});

export default ConcatTwoSquares;
